import {
  Box,
  VStack,
  Text,
  Flex,
  Image,
  IconButton,
} from "@chakra-ui/react";
import { Link, useLocation } from "react-router-dom";
import MyWorkspaceIcon from "../../assets/icons/my-workspace-icon.svg";
import AllParticipantsIcon from "../../assets/icons/all-participants-icon.svg";
import CalendarIcon from "../../assets/icons/calendar-icon.svg";
import ReportsIcon from "../../assets/icons/reports-icon.svg";
import AdminCenterIcon from "../../assets/icons/admin-center-icon.svg";
import SidebarIcon from "../../assets/icons/sidebar-icon.svg";
import { canRead, canAccessAdminCenter, getCurrentUserRole, normalizeAppRole } from "../../constants/accessRights";
import "./layout.css";

interface CollapseButtonProps {
  onClick: () => void;
  isCollapsed?: boolean;
}

const CollapseButton = ({
  onClick,
  isCollapsed = false,
}: CollapseButtonProps) => {
  return (
    <IconButton
      onClick={onClick}
      aria-label={isCollapsed ? "Show sidebar" : "Hide sidebar"}
      position="absolute"
      top="70px"
      right={isCollapsed ? "auto" : "2px"}
      left={isCollapsed ? "2px" : "auto"}
      borderRadius="md"
      w="27px"
      h="27px"
      minW="27px"
      zIndex={10}
      p={0}
      variant={"ghost"}
      ml={4}
    >
      <Image
        src={SidebarIcon}
        alt="Sidebar toggle"
        w="24px"
        h="24px"
        rotate={isCollapsed ? "180deg" : "0deg"}
      />
    </IconButton>
  );
};

interface NavItem {
  label: string;
  path: string;
  icon?: React.ReactNode;
}

const navItems: NavItem[] = [
  { label: "My Workspace", path: "/workspace", icon: MyWorkspaceIcon },
  // { label: "Referrals", path: "/referrals", icon: ReferralIcon },
  {
    label: "All Participants",
    path: "/participants",
    icon: AllParticipantsIcon,
  },
  { label: "Calendar", path: "/calendar", icon: CalendarIcon },
  { label: "Reports", path: "/reports", icon: ReportsIcon },
  { label: "Admin Center", path: "/admin", icon: AdminCenterIcon },
];

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  showToggleOnly?: boolean;
}

export default function Sidebar({
  isOpen,
  onToggle,
  showToggleOnly = false,
}: SidebarProps) {
  const location = useLocation();
  const role = normalizeAppRole(getCurrentUserRole());

  if (showToggleOnly) {
    return <CollapseButton onClick={onToggle} isCollapsed={true} />;
  }

  return (
    <Box
      bg="white"
      borderRight="1px solid"
      borderColor="gray.200"
      h="100%"
      overflowY="auto"
      position="relative"
    >
      <CollapseButton onClick={onToggle} isCollapsed={false} />
      <VStack className="sidebar-nav-container">
        {navItems
          .filter((item) => {
            // Safety check: if role is undefined, show all items except admin
            if (!role) return item.path !== "/admin";
            if (item.path === "/admin") return canAccessAdminCenter(role);
            if (item.path === "/reports") return canRead("reports", role);
            return true;
          })
          .map((item) => {
            const isActive =
              location.pathname === item.path ||
              location.pathname.startsWith(item.path + "/");
            return (
              <Link key={item.path} to={item.path}>
                <Flex className={`nav-item ${isActive ? "nav-item-active" : ""}`}>
                  {item.icon && (
                    <Image
                      src={item.icon as string}
                      alt={item.label}
                      className={
                        isActive ? "sidebar-icon-active" : "sidebar-icon"
                      }
                    />
                  )}
                  <Text className="nav-item-text">{item.label}</Text>
                </Flex>
              </Link>
            );
          })}
      </VStack>
    </Box>
  );
}
